/**
 * ClientDetail — Vista detalle de un cliente con 5 pestañas.
 * 
 * Pestañas:
 * 1. Base de Datos Fija (info, credenciales, plan)
 * 2. Fase 1: Métricas
 * 3. Fase 2: Planificación (Calendario + Acciones)
 * 4. Fase 3: Grabación/Producción (Kanban)
 * 5. Fase 4: Edición/Publicación (Copys)
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import {
  ArrowLeft,
  Database,
  BarChart3,
  CalendarDays,
  Video,
  Send,
  Loader2,
  Settings,
  HardDrive,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { cn, getInitials, formatCurrency } from '@/lib/utils';
import { useAppStore } from '@/stores/appStore';
import type { Client } from '@/types';

// ─── Tab Components (lazy-loaded in a real app) ───
import { TabBaseDatos } from '@/components/clients/TabBaseDatos';
import { TabMetricas } from '@/components/clients/TabMetricas';
import { TabPlanificacion } from '@/components/clients/TabPlanificacion';
import { TabProduccion } from '@/components/clients/TabProduccion';
import { TabPublicacion } from '@/components/clients/TabPublicacion';
import { ClientDriveExplorer } from '@/components/clients/ClientDriveExplorer';
import { ClientModal } from '@/components/clients/ClientModal';

const tabs = [
  { id: 'datos', label: 'Base de Datos', icon: Database },
  { id: 'metricas', label: 'Fase 1: Métricas', icon: BarChart3 },
  { id: 'planificacion', label: 'Fase 2: Planificación', icon: CalendarDays },
  { id: 'produccion', label: 'Fase 3: Producción', icon: Video },
  { id: 'publicacion', label: 'Fase 4: Publicación', icon: Send },
  { id: 'drive', label: 'Google Drive', icon: HardDrive },
];

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentPeriod } = useAppStore();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('datos');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    api
      .getClient(id)
      .then((res) => setClient(res.client as Client))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Cliente no encontrado</p>
        <Button variant="outline" onClick={() => navigate('/clientes')} className="mt-4">
          Volver a Clientes
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      {/* ─── Header del Cliente ─── */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/clientes')}
          className="mt-1"
        >
          <ArrowLeft size={20} />
        </Button>

        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white font-bold text-lg"
          style={{ backgroundColor: client.avatar_color }}
        >
          {getInitials(client.name)}
        </div>

        <div className="flex-1">
          <h1 className="text-2xl font-bold">{client.name}</h1>
          {client.business_name && (
            <p className="text-muted-foreground">{client.business_name}</p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <Badge>{client.plan_type}</Badge>
            {client.status === 'inactive' && (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 font-semibold text-xs">
                Inactivo (Baja)
              </Badge>
            )}
            {client.status === 'paused' && (
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 font-semibold text-xs">
                Pausado
              </Badge>
            )}
            <span className="text-sm text-muted-foreground">
              {formatCurrency(client.monthly_fee)}/mes
            </span>
            <span className="text-sm text-muted-foreground">
              — Período: <strong>{currentPeriod}</strong>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {client.google_drive_folder_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(
                  `https://drive.google.com/drive/folders/${client.google_drive_folder_id}`,
                  '_blank'
                )
              }
              className="gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50 font-medium shadow-sm"
              title="Abrir carpeta del cliente en Google Drive"
            >
              <HardDrive size={14} className="text-blue-500" />
              <span className="hidden sm:inline">Drive</span>
              <ExternalLink size={12} className="text-blue-400" />
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditModalOpen(true)}
            className="gap-1"
          >
            <Settings size={14} />
            Editar
          </Button>
        </div>
      </div>

      {/* Modal Editar Cliente */}
      <ClientModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        initialClient={client}
        onSuccess={(updated) => setClient(updated)}
        onDelete={() => navigate('/clientes')}
      />

      {/* ─── Tabs de Fases ─── */}
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List className="flex border-b overflow-x-auto">
          {tabs.map((tab) => (
            <Tabs.Trigger
              key={tab.id}
              value={tab.id}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
              )}
            >
              <tab.icon size={16} />
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="mt-6">
          <Tabs.Content value="datos">
            <TabBaseDatos client={client} onUpdate={setClient} />
          </Tabs.Content>

          <Tabs.Content value="metricas">
            <TabMetricas clientId={client.id} clientName={client.name} period={currentPeriod} />
          </Tabs.Content>

          <Tabs.Content value="planificacion">
            <TabPlanificacion clientId={client.id} period={currentPeriod} />
          </Tabs.Content>

          <Tabs.Content value="produccion">
            <TabProduccion clientId={client.id} period={currentPeriod} />
          </Tabs.Content>

          <Tabs.Content value="publicacion">
            <TabPublicacion
              clientId={client.id}
              clientName={client.name}
              businessType={client.business_name || ''}
              period={currentPeriod}
            />
          </Tabs.Content>

          <Tabs.Content value="drive">
            <ClientDriveExplorer client={client} onUpdateClient={setClient} />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}
